import React from 'react';
import { updateProjectMemberFn } from 'store/companies/companiesSlice';
import {
  Actions,
  ChangeImplicitButton,
  Item,
  LeftSideItems,
  RightSideItems,
  projectLoadingStyles,
} from './styles';
import messages from '../Messages';
import ActionLink from 'components/ActionLink';
import Input from 'components/Input';
import Icon from 'components/Icon';
import { display } from 'components/ErrorView/ErrorDialog';
import { LoadingIcon } from 'components/LoadingIcon/LoadingIcon';
import APopover from 'components/APopover';
import Select from 'components/Select';
import { IOption } from 'components/Select/Select';
import Chevron from 'components/Chevron';
import CollapsibleBody from 'components/Collapsible/CollapsibleBody';
import {
  LimitationUsersOfRoleRow,
  UserRoleBadges,
} from 'components/Limitation/LimitationManager/LimitationRoleRow/styles';
import Badge from 'components/Badge';
import { convertNumbersToProperFormat } from 'utils/number';
import { ProjectMemberMigrate } from '../ProjectMemberMigrate';
import {
  ICompany,
  ICompanyRole,
  IProjectMemberView,
  IProjectStore,
  IUserRoleView,
  ProjectMemberConfig,
} from 'store/companies/companiesTypes';
import { IUser } from 'store/users/usersType';
import { IUsers } from 'store/store';
import { notifyError } from '../../../../components/Notify/Notify';
import { black } from 'utils/style/colors';

interface IComponentProps {
  member: IProjectMemberView;
  projectRoleMembers?: { [key: string]: IProjectMemberView };
  roles: ICompany['roles'];
  companyMembers: ICompany['members'];
  onDelete: (userId: number) => void;
  user: IUser | ICompanyRole;
  users?: IUsers['map'];
  isRole?: boolean;
  projectTitle?: string;
  project: IProjectStore;
  notOwnerButIsProjectAdmin?: boolean;
  onUpdateMemberStatus?: (
    userId: number,
    type: ProjectMemberConfig,
    value: string | boolean | number | null
  ) => ReturnType<typeof updateProjectMemberFn>;
  addMember?: (
    projectMember: IProjectMemberView
  ) => Promise<IProjectMemberView>;
  updateMember?: (
    projectMember: IProjectMemberView
  ) => Promise<IProjectMemberView>;
}

interface IComponentStates {
  customTitle: string;
  loading: ProjectMemberConfig | 'title' | null;
  showPopover: boolean;
  isOpen: boolean;
}

const projectMemberConfigCheckboxes: ProjectMemberConfig[] = [
  'canCreate',
  'visibleAllTasks',
  'admin',
];

class ProjectMemberView extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state = {
    customTitle: this.props.member.title!,
    loading: null,
    showPopover: false,
    isOpen: false,
  };

  handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const customTitle = e.target.value;
    this.setState({ customTitle });
  };

  handleChangeCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as ProjectMemberConfig;
    const value = e.target.checked;
    this.setState({ loading: name });
    this.props
      .onUpdateMemberStatus?.(this.props.user.id, name, value)
      .then(() => this.setState({ loading: null }))
      .catch((err: any) => {
        this.setState({ loading: null });
        display(err);
      });
  };

  handleChangeImplicit = (value: boolean) => () => {
    this.setState({ loading: 'implicit' });
    this.props
      .onUpdateMemberStatus?.(this.props.user.id, 'implicit', value)
      .then(() => this.setState({ loading: null }))
      .catch((err: any) => {
        this.setState({ loading: null });
        display(err);
      });
  };

  handleUpdateTitle = () => {
    let customTitle: string | null = this.state.customTitle;
    if (this.state.customTitle === '') {
      customTitle = null;
    }
    this.setState({ loading: 'title' });
    this.props
      .onUpdateMemberStatus?.(this.props.user.id, 'title', customTitle)
      .then(
        () => {
          this.setState({ showPopover: false, loading: null });
        },
        (e) => {
          notifyError(e);
          this.setState({ showPopover: false, loading: null });
        }
      );
  };
  handleChangeRelatedRole = (values: IOption[]) => {
    const relatedRoleId: number | null = values.length
      ? Number(values[0].value!)
      : null;
    this.setState({ loading: 'relatedRoleId' });
    this.props
      .onUpdateMemberStatus?.(
        this.props.user.id,
        'relatedRoleId',
        relatedRoleId
      )
      .then(() => {
        this.setState({ showPopover: false, loading: null });
      });
  };
  handleChangeProjectRole = (values: IOption[]) => {
    const projectRoleId: number | null = values.length
      ? Number(values[0].value!)
      : null;
    this.setState({ loading: 'projectRoleId' });
    this.props
      .onUpdateMemberStatus?.(
        this.props.user.id,
        'projectRoleId',
        projectRoleId
      )
      .then(() => {
        this.setState({ showPopover: false, loading: null });
      });
  };

  handleDeleteMember = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      const result = window.confirm(messages.areYouSure);
      if (result) {
        this.props.onDelete(this.props.user.id);
      }
    }
  };

  handleShowPopover = () => {
    this.setState((prevState) => ({ showPopover: !prevState.showPopover }));
  };

  render() {
    const {
      user,
      member,
      projectRoleMembers,
      companyMembers,
      roles,
      projectTitle,
      notOwnerButIsProjectAdmin,
      isRole,
      users,
      project,
    } = this.props;

    const { loading, isOpen } = this.state;
    const userId = user.id;
    const isImplicit = member.implicit;
    const inactive = !isRole && companyMembers[user.id].inactive;

    const uniqueUsersIdList = (user as ICompanyRole)?.users?.reduce(
      (acc: number[], item: IUserRoleView) => {
        if (!acc.includes(item.userId)) {
          acc.push(item.userId);
        }
        return acc;
      },
      []
    );

    const namePart = (
      <RightSideItems style={{ display: 'flex', color: black }}>
        {projectTitle}
        {projectTitle && isRole
          ? ` ( ${roles[member.roleId!]?.name ?? ''} )`
          : ''}
        {!projectTitle &&
          (user?.name !== ''
            ? (user?.[isRole ? 'displayName' : 'name'] ?? '')
            : userId)}
        {inactive ? ` (${messages.inactive})` : ''}
        {!projectTitle && isRole
          ? messages.roleMemberCount(
              convertNumbersToProperFormat((uniqueUsersIdList || []).length)
            )
          : ''}
      </RightSideItems>
    );
    return (
      <Item hasChevron={isRole && !projectTitle}>
        {!isRole && namePart}
        <LeftSideItems>
          {!isRole ? (
            // TODO: update value on update
            <Select
              disabled={loading === 'relatedRoleId'}
              onChangeValues={this.handleChangeRelatedRole}
              values={
                member.relatedRoleId
                  ? [
                      {
                        value: member.relatedRoleId,
                        label:
                          roles[member.relatedRoleId]?.displayName ??
                          companyMembers[user.id]?.roles[member.relatedRoleId]
                            ?.name ??
                          '',
                      },
                    ]
                  : []
              }
              label={messages.roleInProject}
              options={
                Object.values(companyMembers[user.id]?.roles || []).map(
                  (item) => ({
                    value: item.id,
                    label: item.name!,
                  })
                ) || []
              }
              placeholder={messages.roleInProject}
              round={true}
              containerStyle={{ marginLeft: '5px', width: '150px' }}
            />
          ) : (
            <>
              {!projectTitle && (
                <Chevron
                  style={{ width: '35px' }}
                  isOpen={isOpen}
                  onClick={() => this.setState({ isOpen: !isOpen })}
                />
              )}
              <div style={{ width: '150px' }}>{namePart}</div>
            </>
          )}
          <Select
            disabled={loading === 'projectRoleId' || (!!projectTitle && isRole)}
            onChangeValues={this.handleChangeProjectRole}
            values={
              member.projectRoleId
                ? [
                    {
                      value: member.projectRoleId,
                      label: roles[member.projectRoleId]?.name ?? '',
                    },
                  ]
                : []
            }
            options={
              Object.values(roles)
                ?.filter(
                  (it?: ICompanyRole) =>
                    it &&
                    it.isProjectRole &&
                    (!it.projectTags ||
                      !it.projectTags.length ||
                      !!it?.projectTags?.filter((it: string) =>
                        project.tags?.includes(it)
                      )?.length)
                )
                ?.reduce((current, item?: ICompanyRole) => {
                  current!.push({
                    value: item!.id,
                    label: item!.name,
                  });
                  return current!;
                }, [] as IOption[]) ?? []
            }
            label={messages.projectRole}
            placeholder={
              !member.projectRoleId && member.title
                ? member.title
                : messages.projectRole
            }
            round={true}
            containerStyle={{ marginLeft: '5px', width: '150px' }}
          />
          {/*<span>
            <APopover
              arrow
              disabled={inactive || (isRole && projectTitle)}
              curveBorder
              placement="bottom-start"
              trigger="click"
              target={
                <ActionLink
                  color="blue"
                  onClick={this.handleShowPopover}
                  style={{ width: '150px' }}
                >
                  <Icon
                    type="edit"
                    disabled={inactive || (isRole && projectTitle)}
                    style={{ margin: '0px 10px' }}
                  />{' '}
                  {member.title || messages.enterCustomTitle}
                </ActionLink>
              }
            >
              <div>
                <Input
                  id="customTitle"
                  type="text"
                  name="customTitle"
                  value={this.state.customTitle}
                  onChange={this.handleChangeInput}
                  label={messages.enterCustomTitle}
                  placeholder={messages.customTitle}
                />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    color="green"
                    loading={loading === 'title'}
                    onClick={this.handleUpdateTitle}
                  >
                    {messages.save}
                  </Button>
                  <ActionLink color="red" onClick={this.handleShowPopover}>
                    <Icon type="x" />
                  </ActionLink>
                </div>
              </div>
            </APopover>
          </span>*/}
          {!(inactive || (isRole && projectTitle)) ? (
            <ChangeImplicitButton
              onClick={
                !(inactive || (isRole && projectTitle))
                  ? this.handleChangeImplicit(!isImplicit)
                  : undefined
              }
              isImplicit={isImplicit}
            >
              <span>
                {loading === 'implicit' ? (
                  <LoadingIcon
                    color="blue"
                    customStyle={projectLoadingStyles}
                  />
                ) : (
                  <Icon type="plus" />
                )}
              </span>
              <span>{messages.changeImplicitMember(isImplicit)}</span>
            </ChangeImplicitButton>
          ) : (
            <div style={{ width: '200px' }} />
          )}
          {this.props.onUpdateMemberStatus &&
            projectMemberConfigCheckboxes.map((key) => {
              return (
                <span key={`${userId}_${key}_${user.id}`}>
                  <Input
                    disabled={Boolean(
                      inactive ||
                        (key === 'admin' && notOwnerButIsProjectAdmin) ||
                        (isRole && projectTitle)
                    )}
                    checked={Boolean(this.props.member[key])}
                    id={`${member.projectId}_${userId}_${key}_${member.userId}_${member.roleId}`}
                    label={messages[key]}
                    loading={loading === key}
                    name={key}
                    onChange={this.handleChangeCheckbox}
                    type="checkbox"
                  />
                </span>
              );
            })}
          {this.props.onUpdateMemberStatus &&
            !(notOwnerButIsProjectAdmin || (isRole && projectTitle)) && (
              <Actions>
                <span>
                  <ActionLink color="red" onClick={this.handleDeleteMember}>
                    <Icon type="trash-2" />
                  </ActionLink>
                </span>
                {!isRole &&
                  projectRoleMembers &&
                  this.props.updateMember &&
                  this.props.addMember && (
                    <span>
                      <APopover
                        arrow
                        curveBorder
                        placement="bottom-start"
                        trigger="click"
                        target={
                          <ActionLink color="green">
                            <Icon type="corner-left-up" />
                          </ActionLink>
                        }
                      >
                        <div>
                          <ProjectMemberMigrate
                            member={member}
                            roleMembers={projectRoleMembers}
                            roles={roles}
                            userRoles={companyMembers[user.id]?.roles || []}
                            updateMember={this.props.updateMember}
                            addMember={this.props.addMember}
                          />
                        </div>
                      </APopover>
                    </span>
                  )}
              </Actions>
            )}
        </LeftSideItems>
        {!projectTitle && isRole && (
          <RightSideItems>
            <LimitationUsersOfRoleRow
              key={`${user.id}_users`}
              isActive={isOpen}
            >
              <td colSpan={5}>
                <CollapsibleBody isOpen={isOpen}>
                  <UserRoleBadges>
                    {uniqueUsersIdList?.map((uid: number) => {
                      const user = Object.values(users!).find(
                        (u) => u!.id === uid
                      )!;
                      return (
                        <Badge
                          key={`${user.id}_${uid}`}
                          hollow={true}
                          radius={4}
                          color="gray"
                        >
                          {user.name}
                        </Badge>
                      );
                    })}
                  </UserRoleBadges>
                </CollapsibleBody>
              </td>
            </LimitationUsersOfRoleRow>
          </RightSideItems>
        )}
      </Item>
    );
  }
}

export default ProjectMemberView;
